// askSmart-ui/src/pages/TOSP/components/TospStatusBar.tsx
import React, { useState } from "react";
import { useTospData } from "../TospDataContext";
import {
  inspectLocalData,
  localDataHandled,
  markLocalDataHandled,
  migrateLocalDataToBackend,
  MigrationSummary,
} from "../tospMigration";

const describe = (summary: MigrationSummary): string =>
  [
    `${summary.buildings} building${summary.buildings === 1 ? "" : "s"}`,
    `${summary.units} unit${summary.units === 1 ? "" : "s"}`,
    `${summary.taxYears} tax year${summary.taxYears === 1 ? "" : "s"}`,
    `${summary.tenants} tenant${summary.tenants === 1 ? "" : "s"}`,
    `${summary.occupancies} occupanc${summary.occupancies === 1 ? "y" : "ies"}`,
    `${summary.budgets} budget${summary.budgets === 1 ? "" : "s"}`,
  ].join(", ");

// Save errors and the one-time localStorage import, above the tabs. Both are
// module-wide: a failed save on any tab surfaces here rather than being
// swallowed by the tab that attempted it.
export default function TospStatusBar() {
  const { error, clearError, reload } = useTospData();
  const [local] = useState(inspectLocalData);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<MigrationSummary | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  // Persisted, not component state — otherwise the prompt returns on every
  // refresh, since the local records are kept as a backup after the import.
  const [dismissed, setDismissed] = useState(localDataHandled);

  const dismiss = () => {
    markLocalDataHandled();
    setDismissed(true);
  };

  // Budgets are counted too, and by now they are the likely case on their own:
  // every other collection moved to the backend months ago, so a browser still
  // holding something is most often holding a budget worked up before budgets
  // followed. Without this the prompt never appears and that work is stranded.
  const hasLocalData =
    local.buildings + local.units + local.tenants + local.budgets > 0 ||
    local.configuration;

  const runImport = async () => {
    setImporting(true);
    setImportError(null);
    try {
      const summary = await migrateLocalDataToBackend();
      setImported(summary);
      await reload();
    } catch (err) {
      // Shown, not just logged — a silent failure here reads as "the button
      // does nothing" while the browser's copy sits there un-imported.
      console.error("[TOSP] migration failed:", err);
      setImportError((err as Error)?.message || "The import failed.");
      // Whatever did land is worth showing, so the grids aren't stale.
      await reload();
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      {error && (
        <div className="tosp-banner tosp-banner-error" role="alert">
          <span>{error}</span>
          <button type="button" className="tosp-link-btn" onClick={clearError}>
            Dismiss
          </button>
        </div>
      )}

      {/* Shown until the browser's copy has been sent up, or dismissed. The
          import is explicit because it overwrites the server with this
          machine's data. */}
      {hasLocalData && !dismissed && (
        <div className={`tosp-banner${importError ? " tosp-banner-error" : ""}`}>
          {imported ? (
            <span>Imported {describe(imported)} from this browser.</span>
          ) : importError ? (
            // Nothing local is cleared on failure, so re-running after fixing
            // the cause is safe — ids are preserved, so it overwrites.
            <span>{importError} Nothing was lost — this browser still holds its copy ({describe(local)}). Fix the cause and import again.</span>
          ) : (
            <span>
              This browser still holds TOSP data ({describe(local)}). Import it into the
              backend so it's shared and safe.
            </span>
          )}
          {!imported && (
            <button
              type="button"
              className="tosp-btn-secondary"
              disabled={importing}
              onClick={runImport}
            >
              {importing ? "Importing…" : importError ? "Try again" : "Import to backend"}
            </button>
          )}
          <button type="button" className="tosp-link-btn" onClick={dismiss}>
            Dismiss
          </button>
        </div>
      )}
    </>
  );
}
